import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useNotifications } from "@/context/NotificationsContext";
import { toast } from "sonner";
import PortalShell from "@/portal/PortalShell";
import { usePortal } from "@/portal/PortalContext";
import { PageHeader, ErrorState, SkeletonRows, Panel, TitledPanel, IconTile, ArrowLink } from "@/portal/ui/Layout";
import { Segmented } from "@/portal/ui/Tabs";
import { Button } from "@/portal/ui/Button";
import { Modal } from "@/portal/ui/Modal";
import { Bell, FileText, ImageIcon, CheckCheck, ArrowRight, KeyRound, ClipboardCheck, Check, Settings, BarChart3, CircleCheck, CalendarDays, Megaphone } from "lucide-react";
import { timeAgo, fmtDateTime } from "@/portal/format";

const TYPE = {
  document: { icon: FileText, label: "Document", tab: "documente" }, photo: { icon: ImageIcon, label: "Galerie", tab: "galerie" }, report: { icon: FileText, label: "Raport", tab: "documente" },
  access: { icon: KeyRound, label: "Acces", tab: "" }, client_action: { icon: ClipboardCheck, label: "Decizie", tab: "decizii" },
  update: { icon: Megaphone, label: "Actualizare", tab: "decizii" }, meeting: { icon: CalendarDays, label: "Ședință", tab: "calendar" },
};
const PREFS = [
  ["documents", FileText, "Documente noi", "Te anunțăm când sunt publicate documente relevante în proiectele tale."],
  ["updates", BarChart3, "Actualizări de proiect", "Primești informații despre evoluția lucrărilor, etape și ședințe programate."],
  ["decisions", CircleCheck, "Decizii & termene", "Ești notificat când este necesară o decizie din partea ta."],
];

export default function ClientNotifications() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("all");
  const [prefsOpen, setPrefsOpen] = useState(false);
  const { refresh } = useNotifications();

  async function load() {
    setError(null);
    try { const { data } = await api.get("/notifications"); setItems(data); refresh(); } catch (e) { setError(e); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function markAll() {
    try { await api.post("/notifications/read-all"); toast.success("Toate notificările au fost marcate ca citite"); await load(); }
    catch { toast.error("Nu am putut marca notificările. Încearcă din nou."); }
  }
  async function markOne(id) {
    try { await api.post(`/notifications/${id}/read`); await load(); } catch { /* silențios */ }
  }

  const unreadCount = items?.filter((n) => !n.read).length || 0;
  const list = (items || []).filter((n) => view === "all" || !n.read);

  return (
    <PortalShell title="Notificări" crumbs={[{ label: "Acasă", to: "/portal" }, { label: "Notificări" }]}>
      <PageHeader title="Notificări" description={items ? (unreadCount ? `${unreadCount} ${unreadCount === 1 ? "notificare necitită" : "notificări necitite"}.` : "Ești la zi cu noutățile proiectului.") : undefined}
        actions={<>
          {unreadCount > 0 && <Button variant="secondary" onClick={markAll} data-testid="mark-all-read"><CheckCheck /> Marchează toate ca citite</Button>}
          <Button variant="outline" onClick={() => setPrefsOpen(true)} data-testid="notif-prefs-btn"><Settings /> Preferințe notificări</Button>
        </>} />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5 items-start">
        <div>
          <Segmented ariaLabel="Filtru notificări" value={view} onChange={setView} className="mb-4"
            options={[{ value: "all", label: "Toate" }, { value: "unread", label: "Necitite", count: unreadCount || undefined }]} />
          {error && !items ? <ErrorState onRetry={load} /> : items === null ? <SkeletonRows rows={5} /> : list.length === 0 ? (
            <div className="cp-panel py-16 px-6 text-center" data-testid="notif-empty">
              <div className="mx-auto h-24 w-24 rounded-full bg-cp-accent-soft grid place-items-center text-cp-accent mb-5"><Bell className="h-10 w-10" aria-hidden="true" /></div>
              <p className="cp-display !text-[30px]">Ești la zi.</p>
              <p className="cp-body mt-2 max-w-md mx-auto">{view === "unread" && items.length ? "Nu ai notificări necitite." : "Te anunțăm când echipa publică documente, fotografii sau solicită o decizie."}</p>
              <Button as={Link} to="/portal/proiecte" variant="outline" className="mt-6" data-testid="notif-empty-cta">Vezi proiectele <ArrowRight /></Button>
            </div>
          ) : (
            <Panel padded={false} data-testid="notifications-list">
              <ul className="divide-y divide-cp-border">
                {list.map((n) => {
                  const t = TYPE[n.type] || { icon: Bell, label: "Informare", tab: "" };
                  const Icon = t.icon;
                  return (
                    <li key={n.id} className={`flex items-start gap-4 px-4 sm:px-5 py-4 cp-row-hover ${n.read ? "" : "bg-cp-accent-soft/40"}`} data-testid={`notif-${n.id}`}>
                      <span className="relative"><IconTile icon={Icon} tone={n.read ? "neutral" : ""} />{!n.read && <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-cp-amber ring-2 ring-white" aria-label="Necitită" />}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[14px] leading-snug ${n.read ? "text-cp-text-2" : "text-cp-text font-medium"}`}>{n.message}</p>
                        <p className="cp-meta mt-1" title={fmtDateTime(n.created_at)}>{t.label} · {timeAgo(n.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!n.read && <Button variant="ghost" size="icon-sm" onClick={() => markOne(n.id)} aria-label="Marchează ca citită" data-testid={`notif-read-${n.id}`}><Check /></Button>}
                        {n.project_id && <Button as={Link} to={`/portal/proiecte/${n.project_id}${t.tab ? `?tab=${t.tab}` : ""}`} variant="ghost" size="icon-sm" aria-label="Deschide proiectul" data-testid={`notif-open-${n.id}`}><ArrowRight /></Button>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          )}
        </div>

        <TitledPanel title="Ce primești aici" testid="notif-info">
          <ul className="space-y-4">
            {PREFS.map(([k, Icon, t, d]) => <li key={k} className="flex items-start gap-3"><IconTile icon={Icon} tone="neutral" /><div><div className="text-[13.5px] font-semibold text-cp-text">{t}</div><p className="cp-meta mt-0.5 leading-relaxed">{d}</p></div></li>)}
          </ul>
          <div className="mt-5 pt-4 border-t border-cp-border"><ArrowLink onClick={() => setPrefsOpen(true)} testid="notif-prefs-link">Gestionează preferințele</ArrowLink></div>
        </TitledPanel>
      </div>

      <PrefsModal open={prefsOpen} onOpenChange={setPrefsOpen} />
    </PortalShell>
  );
}

function PrefsModal({ open, onOpenChange }) {
  const { overview, refresh } = usePortal();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPrefs(overview?.notification_prefs || { documents: true, updates: true, decisions: true }); }, [open, overview]);

  async function save() {
    setBusy(true);
    try { await api.put("/client/notification-prefs", prefs); toast.success("Preferințele au fost salvate"); refresh(); onOpenChange(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  if (!prefs) return null;
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Preferințe notificări" testid="prefs-modal" size="sm"
      description="Alege pentru ce evenimente vrei să primești și un email. Notificările din portal rămân mereu active."
      footer={<><Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>Renunță</Button><Button onClick={save} loading={busy} data-testid="prefs-save">Salvează</Button></>}>
      <ul className="divide-y divide-cp-border">
        {PREFS.map(([k, Icon, t, d]) => (
          <li key={k} className="flex items-center gap-3 py-3.5">
            <IconTile icon={Icon} tone="neutral" size="sm" />
            <div className="flex-1 min-w-0"><div className="text-[13.5px] font-semibold text-cp-text">{t}</div><div className="cp-meta">{d}</div></div>
            <button type="button" role="switch" aria-checked={!!prefs[k]} aria-label={`Email pentru ${t}`} onClick={() => setPrefs({ ...prefs, [k]: !prefs[k] })} className="cp-switch" data-testid={`pref-${k}`} />
          </li>
        ))}
      </ul>
    </Modal>
  );
}
